import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { ApiError, publicApi } from '@/lib/api'
import { dateTime } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { CodemaxBrand } from '@/components/Brand'
import { Button } from '@/components/ui/primitives'
import { Card } from '@/components/ui/primitives'
import { Input } from '@/components/ui/form'
import { Spinner } from '@/components/ui/primitives'
import type { AcceptanceVerification } from '@/types/api'

/**
 * Where an accepted quotation can be checked by somebody who has only the paper.
 *
 * <p>
 * The reason this exists: a document that says "accepted online by so-and-so" is asserting
 * its own authenticity, and whoever can edit the page can edit the sentence. The reference
 * printed beside it resolves here, against the system that issued the document - which is
 * the part a forged copy cannot reproduce.
 * </p>
 *
 * Anonymous, like the acceptance page, and for the same reason: the customer's auditor has
 * no account and never will. It states facts about a document the asker already holds - no
 * amounts, no lines, no copy of the quotation, nothing to act on.
 */
export function VerifyAcceptance() {
  const { reference } = useParams()

  return (
    <div className="min-h-full bg-wash">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 px-4 py-10">
        <CodemaxBrand className="h-8 self-start" />
        {reference ? <Result reference={reference} /> : <Lookup />}
      </div>
    </div>
  )
}

/** With no reference in the address, somebody has to type one. */
function Lookup() {
  const [typed, setTyped] = useState('')

  const go = () => {
    const trimmed = typed.trim()
    if (trimmed.length > 0) window.location.assign(`/verify/${encodeURIComponent(trimmed)}`)
  }

  return (
    <Card>
      <div className="flex flex-col gap-4 px-6 py-6">
        <div>
          <h1 className="text-[17px] font-semibold">Check an accepted quotation</h1>
          <p className="mt-1.5 text-[13px] text-muted">
            Enter the reference printed under the signature, for example{' '}
            <span className="font-mono text-[12px]">QTA-8K3M-7QD2-R94F</span>. It confirms who
            accepted the quotation, when, and whether it still stands.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={typed}
            placeholder="QTA-0000-0000-0000"
            className="max-w-xs font-mono"
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') go()
            }}
          />
          <Button variant="primary" disabled={typed.trim().length === 0} onClick={go}>
            Check
          </Button>
        </div>
      </div>
    </Card>
  )
}

function Result({ reference }: { reference: string }) {
  const verification = useAsync<AcceptanceVerification>(
    (signal) =>
      publicApi.get<AcceptanceVerification>(
        `/public/verify/${encodeURIComponent(reference)}`,
        signal,
      ),
    [reference],
  )

  if (verification.loading) {
    return (
      <Card>
        <div role="status" className="flex items-center justify-center gap-2.5 py-16 text-muted">
          <Spinner className="size-4" label={null} />
          <span className="text-[13px]">Checking...</span>
        </div>
      </Card>
    )
  }

  // A reference that matches nothing and one that is not a reference at all get the same
  // answer, because the difference would tell somebody guessing which shape to try next.
  if (verification.error || !verification.data) {
    const problem = verification.error

    return (
      <Card>
        <div className="px-6 py-8">
          <h1 className="text-[17px] font-semibold">
            {problem instanceof ApiError ? problem.title : 'No acceptance matches that reference'}
          </h1>
          <p className="mt-2 max-w-prose text-[13px] text-muted">
            {problem instanceof ApiError && problem.detail
              ? problem.detail
              : 'Check the reference printed on the quotation, including the letters before the first dash.'}
          </p>
          <p className="mt-4">
            <a href="/verify" className="text-[13px] font-medium text-teal underline-offset-2 hover:underline">
              Try another reference
            </a>
          </p>
        </div>
      </Card>
    )
  }

  const record = verification.data
  const cancelled = record.status.toLowerCase().includes('cancelled')

  return (
    <Card>
      <div className="px-6 py-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
          {record.reference}
        </p>

        <h1 className={`mt-1 text-[17px] font-semibold ${cancelled ? 'text-crit' : 'text-teal'}`}>
          {cancelled
            ? 'Accepted, then cancelled'
            : 'Confirmed - this acceptance is on record'}
        </h1>

        <p className="mt-1.5 max-w-prose text-[13px] text-muted">
          {cancelled
            ? 'The quotation below was accepted, and has since been cancelled. Speak to your Codemax contact before acting on the copy you are holding.'
            : 'The quotation below was accepted electronically. This page is the issuing system, not the document.'}
        </p>

        <dl className="mt-5 grid grid-cols-[9rem_1fr] gap-x-4 gap-y-2 text-[13px]">
          <Row label="Quotation">
            {record.quotationNo ?? 'Not numbered'}
            {record.revisionNo > 1 && ` · revision ${record.revisionNo}`}
          </Row>
          <Row label="Issued by">{record.issuedBy}</Row>
          <Row label="Accepted by">
            {record.signerName}
            {record.signerJobTitle && `, ${record.signerJobTitle}`} for {record.customerName}
          </Row>
          <Row label="Signed">{dateTime(record.signedAt)}</Row>
          <Row label="Status">{record.status}</Row>

          {record.quotationSha256 && (
            <Row label="Quotation fingerprint" mono>
              {record.quotationSha256}
            </Row>
          )}

          {record.termsSha256 && (
            <Row label="Terms fingerprint" mono>
              {record.termsDocumentName && (
                <span className="block font-sans text-muted">
                  {record.termsDocumentName}
                  {record.termsVersionNo ? ` · version ${record.termsVersionNo}` : ''}
                </span>
              )}
              {record.termsSha256}
            </Row>
          )}
        </dl>

        <p className="mt-5 max-w-prose text-[12px] text-muted">
          The fingerprints are SHA-256 of the quotation as it was accepted and of the terms
          document attached to it. They let either side show the document has not been altered
          since. No amounts are shown here.
        </p>
      </div>
    </Card>
  )
}

function Row({
  label,
  mono,
  children,
}: {
  label: string
  mono?: boolean
  children: React.ReactNode
}) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className={mono ? 'break-all font-mono text-[11.5px]' : ''}>{children}</dd>
    </>
  )
}
